import type { Brand } from './brand.ts'

/**
 * Time in this app is *civil* time in one fixed family timezone, not the device's.
 * A tablet that travels, a phone with the wrong timezone, or a child changing the
 * clock must not silently move a routine into another day.
 *
 * The "logical day" also does not start at midnight: the evening routine can finish
 * at 00:10, and that still belongs to the day that is ending. The cutoff (04:00 by
 * default) is what makes the streak survive a late bedtime.
 *
 * Only `Intl` is used here, which is ECMA-402 and available in Node as well as the
 * browser, so this module stays testable without a DOM.
 */

/** A logical day, `YYYY-MM-DD`. */
export type DayId = Brand<string, 'DayId'>
/** An ISO week-numbering week, `YYYY-Www`. */
export type WeekId = Brand<string, 'WeekId'>

export interface CalendarConfig {
  /** IANA timezone of the family, e.g. `Europe/Madrid`. Never the device's. */
  readonly timezone: string
  /** Hour (0-23) of local time at which a new logical day begins. */
  readonly dayCutoffHour: number
}

export const DEFAULT_CALENDAR: CalendarConfig = {
  timezone: 'Europe/Madrid',
  dayCutoffHour: 4,
}

/**
 * What kind of day it is. `weekday` and `weekend` come from the calendar; the rest
 * are declared by a parent (`DayTypeDeclared`) and are the "written exceptions" the
 * study asks for, so that a holiday or a sick day never has to be argued about.
 */
export type DayKind = 'weekday' | 'weekend' | 'holiday' | 'vacation' | 'special' | 'sick' | 'paused'

/** Which screen-time budget a day kind draws from. `none` means no chest at all. */
export type ScreenBudget = 'weekday' | 'weekend' | 'none'

const DAY_ID_PATTERN = /^\d{4}-\d{2}-\d{2}$/

// --- Civil date arithmetic (no Date involved, so DST cannot corrupt it) -------
// Howard Hinnant's days-from-civil / civil-from-days.

export function daysFromCivil(year: number, month: number, day: number): number {
  const y = year - (month <= 2 ? 1 : 0)
  const era = Math.floor((y >= 0 ? y : y - 399) / 400)
  const yoe = y - era * 400
  const doy = Math.floor((153 * (month + (month > 2 ? -3 : 9)) + 2) / 5) + day - 1
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy
  return era * 146097 + doe - 719468
}

export function civilFromDays(days: number): readonly [number, number, number] {
  const z = days + 719468
  const era = Math.floor((z >= 0 ? z : z - 146096) / 146097)
  const doe = z - era * 146097
  const yoe = Math.floor(
    (doe - Math.floor(doe / 1460) + Math.floor(doe / 36524) - Math.floor(doe / 146096)) / 365,
  )
  const y = yoe + era * 400
  const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100))
  const mp = Math.floor((5 * doy + 2) / 153)
  const d = doy - Math.floor((153 * mp + 2) / 5) + 1
  const m = mp + (mp < 10 ? 3 : -9)
  return [y + (m <= 2 ? 1 : 0), m, d]
}

const pad2 = (n: number): string => (n < 10 ? `0${n}` : String(n))

// --- DayId -------------------------------------------------------------------

export function isDayId(value: string): boolean {
  if (!DAY_ID_PATTERN.test(value)) return false
  const [y, m, d] = value.split('-').map(Number) as [number, number, number]
  if (m < 1 || m > 12 || d < 1 || d > 31) return false
  const [ry, rm, rd] = civilFromDays(daysFromCivil(y, m, d))
  return ry === y && rm === m && rd === d
}

export function asDayId(value: string): DayId {
  if (!isDayId(value)) throw new RangeError(`Not a valid day id: ${value}`)
  return value as DayId
}

export function dayIdOf(year: number, month: number, day: number): DayId {
  return asDayId(`${String(year).padStart(4, '0')}-${pad2(month)}-${pad2(day)}`)
}

export function parseDayId(day: DayId): readonly [number, number, number] {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number]
  return [y, m, d]
}

function dayIdFromDays(days: number): DayId {
  const [y, m, d] = civilFromDays(days)
  return dayIdOf(y, m, d)
}

export function addDays(day: DayId, amount: number): DayId {
  const [y, m, d] = parseDayId(day)
  return dayIdFromDays(daysFromCivil(y, m, d) + amount)
}

/** Whole days from `from` to `to`; negative when `to` is earlier. */
export function diffDays(from: DayId, to: DayId): number {
  const [fy, fm, fd] = parseDayId(from)
  const [ty, tm, td] = parseDayId(to)
  return daysFromCivil(ty, tm, td) - daysFromCivil(fy, fm, fd)
}

export function compareDays(a: DayId, b: DayId): number {
  return a < b ? -1 : a > b ? 1 : 0
}

/** ISO weekday, 1 = Monday through 7 = Sunday. */
export function isoDayOfWeek(day: DayId): number {
  const [y, m, d] = parseDayId(day)
  return ((((daysFromCivil(y, m, d) + 3) % 7) + 7) % 7) + 1
}

export function isWeekend(day: DayId): boolean {
  return isoDayOfWeek(day) >= 6
}

/** The Monday of the week containing `day`. Weeks start on Monday, always. */
export function weekStart(day: DayId): DayId {
  return addDays(day, 1 - isoDayOfWeek(day))
}

/** ISO-8601 week-numbering week, e.g. `2026-W38`. */
export function weekId(day: DayId): WeekId {
  const [y, m, d] = parseDayId(day)
  const z = daysFromCivil(y, m, d)
  const thursday = z - isoDayOfWeek(day) + 4
  const [weekYear] = civilFromDays(thursday)
  const jan4 = daysFromCivil(weekYear, 1, 4)
  const jan4Dow = ((((jan4 + 3) % 7) + 7) % 7) + 1
  const firstThursday = jan4 - jan4Dow + 4
  const week = Math.floor((thursday - firstThursday) / 7) + 1
  return `${weekYear}-W${pad2(week)}` as WeekId
}

// --- Timezone conversion -----------------------------------------------------

const formatters = new Map<string, Intl.DateTimeFormat>()

function formatterFor(timezone: string): Intl.DateTimeFormat {
  const cached = formatters.get(timezone)
  if (cached) return cached
  const created = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
  formatters.set(timezone, created)
  return created
}

export interface WallClock {
  readonly year: number
  readonly month: number
  readonly day: number
  readonly hour: number
  readonly minute: number
  readonly second: number
}

/** The wall-clock reading a person in `timezone` would see at `instant`. */
export function wallClock(instant: Date, timezone: string): WallClock {
  const parts = formatterFor(timezone).formatToParts(instant)
  const read = (type: Intl.DateTimeFormatPartTypes): number => {
    const found = parts.find((p) => p.type === type)
    if (!found) throw new Error(`Missing ${type} for timezone ${timezone}`)
    return Number(found.value)
  }
  return {
    year: read('year'),
    month: read('month'),
    day: read('day'),
    hour: read('hour'),
    minute: read('minute'),
    second: read('second'),
  }
}

function offsetMs(instant: Date, timezone: string): number {
  const w = wallClock(instant, timezone)
  const asUtc = Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second)
  return asUtc - Math.floor(instant.getTime() / 1000) * 1000
}

/**
 * The instant at which the given wall-clock reading happens in `timezone`.
 * Two passes so that a DST boundary converges; during a spring-forward gap the
 * result lands on the instant the clock jumps to, which is what a person means.
 */
export function instantFromWallClock(w: WallClock, timezone: string): Date {
  const asUtc = Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second)
  let ts = asUtc - offsetMs(new Date(asUtc), timezone)
  ts = asUtc - offsetMs(new Date(ts), timezone)
  return new Date(ts)
}

// --- Logical day -------------------------------------------------------------

/** The logical day an instant belongs to, honouring the family cutoff hour. */
export function logicalDay(instant: Date, calendar: CalendarConfig = DEFAULT_CALENDAR): DayId {
  const w = wallClock(instant, calendar.timezone)
  const days = daysFromCivil(w.year, w.month, w.day)
  return dayIdFromDays(w.hour < calendar.dayCutoffHour ? days - 1 : days)
}

/** The instant a logical day begins. */
export function dayStart(day: DayId, calendar: CalendarConfig = DEFAULT_CALENDAR): Date {
  const [year, month, dayOfMonth] = parseDayId(day)
  return instantFromWallClock(
    { year, month, day: dayOfMonth, hour: calendar.dayCutoffHour, minute: 0, second: 0 },
    calendar.timezone,
  )
}

/** The instant a logical day ends, which is when the next one begins. */
export function dayEnd(day: DayId, calendar: CalendarConfig = DEFAULT_CALENDAR): Date {
  return dayStart(addDays(day, 1), calendar)
}

/** Real elapsed milliseconds in a logical day. 23h or 25h on DST transitions. */
export function dayLengthMs(day: DayId, calendar: CalendarConfig = DEFAULT_CALENDAR): number {
  return dayEnd(day, calendar).getTime() - dayStart(day, calendar).getTime()
}

// --- Day kinds ---------------------------------------------------------------

/** What the calendar alone says, before any parent declaration. */
export function baseDayKind(day: DayId): DayKind {
  return isWeekend(day) ? 'weekend' : 'weekday'
}

/**
 * Which screen budget a day draws from. Holidays, school breaks and special days
 * use the weekend budget; a sick or paused day has none, and by design that is not
 * a punishment: a paused day is skipped by the streak rather than breaking it.
 */
export function screenBudgetFor(kind: DayKind): ScreenBudget {
  switch (kind) {
    case 'weekday':
      return 'weekday'
    case 'weekend':
    case 'holiday':
    case 'vacation':
    case 'special':
      return 'weekend'
    case 'sick':
    case 'paused':
      return 'none'
  }
}

/** A day that neither advances nor breaks a streak. */
export function isSkippedByStreak(kind: DayKind): boolean {
  return kind === 'paused' || kind === 'sick'
}
